import React from "react";
import { CodedProperty, depascalize, getUniqueArrayItems, Icon, IconText, Stack, TooltipHost } from "sprint-common-web";

import { DynamicSubtitleType } from "../../../../common/types/DynamicSubtitleType";
import { getLanguageDisplayName } from "../../../../common/types/Language";
import { RecordType } from "../../../../common/types/RecordType";
import { OrderAssetAvailability } from "../../types/OrderAssetAvailability";
import { OrderAssetStorageType } from "../../types/OrderAssetStorageType";
import { getAssetAvailabilityIconName } from "../../utils/getAssetAvailabilityIconName";
import { getAssetAvailabilityColor } from "../../utils/getAvailabilityColor";
import { getEmbeddedInfo } from "../../utils/getEmbeddedInfo";

interface AssetLanguage {
    availability: OrderAssetAvailability;
    language: CodedProperty;
    isEmbedded?: boolean;
    storageType?: OrderAssetStorageType;
}

export interface AssetsAvailabilityListProps {
    items: AssetLanguage[];
    assetType: RecordType;
    requiredSubtitleTypes?: DynamicSubtitleType[];
    type: OrderAssetStorageType;
    isTrailer?: boolean;
    testId?: string;
}

export const AssetsAvailabilityList = ({
    items,
    type,
    assetType,
    requiredSubtitleTypes,
    isTrailer,
    testId
}: AssetsAvailabilityListProps): JSX.Element => (
    <Stack tokens={{ childrenGap: 5 }}>
        {items.map((item) => (
            <LanguageAssetInfo
                item={item}
                storageType={item.storageType ?? type}
                assetType={assetType}
                requiredSubtitleTypes={requiredSubtitleTypes}
                isTrailer={isTrailer}
                testId={testId}
                key={`${item.language.id}-${item.storageType ?? type}`}
                displayText
            />
        ))}
    </Stack>
);

interface LanguageAssetInfoProps {
    item: AssetLanguage;
    assetType: RecordType;
    storageType: OrderAssetStorageType;
    requiredSubtitleTypes?: DynamicSubtitleType[];
    isTrailer?: boolean;
    displayText?: boolean;
    testId?: string;
}

export const LanguageAssetInfo = ({
    item,
    assetType,
    storageType,
    requiredSubtitleTypes,
    displayText,
    isTrailer,
    testId
}: LanguageAssetInfoProps): JSX.Element => {
    const displaySubtitleInfo =
        !isTrailer &&
        !item.isEmbedded &&
        assetType === RecordType.Subtitle &&
        item.availability !== OrderAssetAvailability.FullMatch;

    return (
        <Stack
            horizontal
            tokens={{ childrenGap: 5 }}
            styles={{ root: { height: 20 } }}
            key={`${item.language.id}-${storageType}`}
        >
            <IconText
                testAttributes={{
                    "data-testid": testId,
                    "data-testavailable": item.availability.toString(),
                    "data-testtype": storageType.toString()
                }}
                text={displayText ? formatLanguageText(item.language, item.isEmbedded) : ""}
                iconName={getAssetAvailabilityIconName(item.availability)}
                textVariant="smallPlus"
                iconColor={getAssetAvailabilityColor(item.availability, storageType)}
                openInNewTab
            />
            {displaySubtitleInfo && renderSubtitleInfo(requiredSubtitleTypes)}
        </Stack>
    );
};

const getRequiredSubtitleTypesMessage = (requiredSubtitleTypes: DynamicSubtitleType[]): string => {
    if (!requiredSubtitleTypes.length) return "System does not allow any subtitle types";

    const uniqueTypes = getUniqueArrayItems(
        requiredSubtitleTypes.map((subtitleType) => depascalize(DynamicSubtitleType[subtitleType]))
    );

    const formattedTypes = uniqueTypes.map((type) => `{${type}}`).join(" ");

    return `System requires: ${formattedTypes} subtitle types.`;
};

const formatLanguageText = (language: CodedProperty, isEmbedded?: boolean): string =>
    `${getLanguageDisplayName(language)} ${getEmbeddedInfo(isEmbedded)}`;

const renderSubtitleInfo = (requiredSubtitleTypes?: DynamicSubtitleType[]): React.ReactNode => {
    if (!requiredSubtitleTypes) return null;

    return (
        <TooltipHost
            styles={{ root: { display: "flex", alignItems: "center" } }}
            content={getRequiredSubtitleTypesMessage(requiredSubtitleTypes)}
        >
            <Icon iconName="Info" />
        </TooltipHost>
    );
};
